'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { useWindowVirtualizer } from '@tanstack/react-virtual';

import useResizeObserver from 'use-resize-observer';

import { queryFunctions } from '@hub/common/src/query-functions';
import type { Account } from '@hub/common/src/types/cit-public-api';

import Navbar from '@/components/Navbar';
import CollectionCard from '@/components/CollectionCard';
import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';

const OVERSCAN_MULTIPLIER = 2; // virtualize 'n' additional rows above & 'n' additional rows below the viewport
const MIN_COLUMNS = 1;
// the width a card wants before another column is added; the columns then stretch to fill the row
const WANTED_CARD_WIDTH = 320;
// a card is a fixed box: its 4:2 cover image plus room for the name, item count, location and the
// clamped description, so a row's height is known without measuring anything
const CARD_TEXT_HEIGHT = 160;
const GAP = 24;

export default function CollectionsDirectory({ searchQuery }: { searchQuery: string | null }) {
  const citOrigin = useCitOriginClient();

  const [columnCount, setColumnCount] = useState(1);
  const [cardWidth, setCardWidth] = useState(WANTED_CARD_WIDTH);
  // how far down the page the list starts. The window virtualizer measures against the document,
  // so without this its range is shifted and the last rows never come into it
  const [listOffset, setListOffset] = useState(0);

  const listRef = useRef<HTMLDivElement>(null);

  const { data, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    // same key and function the page seeds, so the first page renders without a refetch
    queryKey: ['paginated-accounts', { searchQuery, citOrigin }],
    initialPageParam: undefined,
    // hidden accounts are filtered out after paging, so a page can be empty with more behind it
    getNextPageParam: (lastPage: { accounts: Account[]; total: number; cursor?: string }) =>
      lastPage.cursor,
    queryFn: queryFunctions.fetchAccountsWithPagination
  });

  const allAccounts = useMemo(() => (data ? data.pages.flatMap((d) => d.accounts) : []), [data]);

  const { width: containerWidth = 0 } = useResizeObserver<HTMLDivElement>({
    // eslint-disable-next-line
    ref: listRef.current
  });

  const cardHeight = cardWidth / 2 + CARD_TEXT_HEIGHT;

  const virtualizer = useWindowVirtualizer({
    count: allAccounts.length,
    estimateSize: () => cardHeight + GAP,
    overscan: columnCount * OVERSCAN_MULTIPLIER,
    scrollMargin: listOffset,
    lanes: columnCount
  });

  useEffect(() => {
    // the list is paged in, so on a reload the browser's restored offset lands in content that
    // isn't there yet. It keeps re-applying that offset as pages arrive, which pins us at the
    // bottom and fetches every remaining page back to back. Start at the top instead
    history.scrollRestoration = 'manual';

    window.scrollTo(0, 0);
  }, [searchQuery]);

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (containerWidth > 0) {
      // how many cards of the wanted width fit, gaps included
      const cols = Math.max(
        MIN_COLUMNS,
        Math.floor((containerWidth + GAP) / (WANTED_CARD_WIDTH + GAP))
      );

      setColumnCount(cols);
      // widen the cards so the row fills the container evenly
      setCardWidth(Math.floor((containerWidth - (cols - 1) * GAP) / cols));

      setListOffset(listRef.current?.offsetTop ?? 0);

      virtualizer.measure();
    }
  }, [containerWidth, virtualizer]);

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      // an all-hidden page renders no rows, so there is no last item to trigger the next fetch
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }

      return;
    }

    if (lastItem.index >= allAccounts.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, virtualItems, allAccounts]);

  return (
    <div className='min-h-full bg-gray-50'>
      <Navbar priorPageURL='/' />

      <div className='container mx-auto px-4 py-8'>
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900'>Collections</h1>
        </div>
        {allAccounts.length > 0 ? (
          <div ref={listRef} style={{ display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                position: 'relative',
                height: `${Math.max(virtualizer.getTotalSize() - GAP, 0)}px`,
                width: `${columnCount * cardWidth + (columnCount - 1) * GAP}px`,
                minHeight: '100px'
              }}
            >
              {virtualItems.map((virtualItem) => {
                const account = allAccounts[virtualItem.index];
                // Skip rendering if we don't have an account and we're not loading more
                if (!account) {
                  return null;
                }

                const col = virtualItem.index % columnCount;

                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'absolute',
                      // need to calculate x position ourselves, y position is handled by virtualizer
                      transform: `translate(${col * (cardWidth + GAP)}px, ${
                        virtualItem.start - listOffset
                      }px)`,
                      width: `${cardWidth}px`,
                      height: `${cardHeight}px`
                    }}
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    className='collection'
                  >
                    <CollectionCard account={account} citOrigin={citOrigin} size={cardWidth} />
                  </div>
                );
              })}
            </div>
          </div>
        ) : !isError ? (
          <div className='text-center text-gray-600'>No collections found</div>
        ) : null}
        {/* in flow under the list rather than parked in a reserved row, so it takes up space only
            while it is actually showing */}
        {/* TODO: replace 'Loading...' with an actual loading symbol */}
        {isFetchingNextPage && !isError ? (
          <div className='py-6 text-center text-gray-600'>Loading...</div>
        ) : null}
        {isError ? (
          <div className='py-6 text-center text-gray-600'>Something went wrong...</div>
        ) : null}
      </div>
    </div>
  );
}
